import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { TransactionState } from '@frontend/transaction/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLicenseRepository } from '../../utils/license-repository';

export interface ManageOverviewPage {
    title: ReactNode;
    route: string;
}

export interface ManageOverviewApplication {
    licensedApplication: Application;
    pages: ManageOverviewPage[];
    type: 'primary' | 'muted';
    nameOverride?: ReactNode;
}

export interface ManageOverviewViewProps {
    filteredApplications: ManageOverviewApplication[];
}

const useManageOverview = (): ManageOverviewViewProps => {
    const { functionalities } = useLicenseRepository();

    const filteredApplications = useMemo(() => {
        const canBulkMove = functionalities?.some((func) => func.application === Application.PUDO && func.feature === Feature.BULK_MOVE_TRANSACTION);

        const pudoPages: ManageOverviewPage[] = [
            {
                title: TransactionState.TRANSACTION_DROP_OFF_PENDING.displayName,
                route: '/spots/layout/pudo/awaiting-drop-off'
            },
            {
                title: TransactionState.TRANSACTION_PICK_UP_PENDING.displayName,
                route: '/spots/layout/pudo/awaiting-pick-up'
            }
        ];

        if (canBulkMove) {
            pudoPages.push({
                title: (
                    <FormattedMessage
                        id='kiosk.manage-spot.bulk-move-transactions.title'
                        defaultMessage='Bulk move transactions'
                        description='Title on the kiosk manage-spot bulk move transactions list screen'
                    />
                ),
                route: '/spots/layout/pudo/bulk-move-transactions'
            });
        }

        const applications: ManageOverviewApplication[] = [
            {
                licensedApplication: Application.PUDO,
                pages: pudoPages,
                type: 'primary'
            },
            {
                licensedApplication: Application.VENDING,
                pages: [
                    {
                        title: CommonMessage.OBJECTS.STOCK.DEFAULT,
                        route: '/spots/layout/vending-stock'
                    }
                ],
                type: 'primary'
            },
            {
                licensedApplication: Application.LENDING,
                pages: [
                    {
                        title: CommonMessage.OBJECTS.STOCK.DEFAULT,
                        route: '/spots/layout/lending-stock'
                    }
                ],
                type: 'primary'
            },
            {
                licensedApplication: Application.ADMIN,
                nameOverride: (
                    <FormattedMessage
                        id='kiosk.manage-spot.links.utilities'
                        defaultMessage='Utilities'
                    />
                ),
                pages: [
                    {
                        title: (
                            <FormattedMessage
                                id='kiosk.manage-spot.links.view-content'
                                defaultMessage='View content'
                            />
                        ),
                        route: '/spots/layout/view-content'
                    },
                    {
                        title: (
                            <FormattedMessage
                                id='kiosk.manage-spot.links.open-slots'
                                defaultMessage='Open SLOTs'
                            />
                        ),
                        route: '/spots/layout/open-slots'
                    }
                ],
                type: 'muted'
            }
        ];

        return applications.filter(
            (app) => app.licensedApplication === Application.ADMIN || functionalities?.some((func) => func.application === app.licensedApplication)
        );
    }, [functionalities]);

    return {
        filteredApplications
    };
};

export default useManageOverview;
